import { useState, useEffect, useMemo, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WeekdayDropdown } from "@/components/weekday-dropdown";
import { Spinner, ErrorDisplay } from "@/components/loading";
import {
  useParticipants,
  useParticipantSurveySchedule,
  useSurveyConfig,
  useUpdateParticipantSurveySchedule,
  useUpdateSurveyConfig,
} from "@/api/hooks";
import { formatDate } from "@/lib/format-date";
import type { SurveySection, SurveyTimingSlot, SurveyRatingItem, SurveyYesNoItem, SurveyOpenEnded, SurveyMultiSelectItem, SurveySingleSelectItem, SurveyTextItem, SurveyItem, SurveySkipCondition } from "@/api/types";

const DEFAULT_OPEN_ENDED: SurveyOpenEnded = {
  enabled: true,
  title: "Anything Else?",
  description: "",
  placeholder: "Type your response here (optional)",
};

// Date.now() alone can collide when two items are added within a millisecond;
// a monotonic counter makes ids unique within the session (and prevents
// duplicate-id 422s from the backend).
let idCounter = 0;
function newId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now()}_${idCounter}`;
}

function deepCopySections(sections: SurveySection[]): SurveySection[] {
  return JSON.parse(JSON.stringify(sections));
}

function normalizeSections(sections: SurveySection[]): SurveySection[] {
  return sections.map((section) => {
    const weekdays = section.weekdays
      ?.map(Number)
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);

    return {
      ...section,
      weekdays: weekdays?.length ? [...new Set(weekdays)].sort((a, b) => a - b) : undefined,
    };
  });
}

function renumberSections(sections: SurveySection[]): SurveySection[] {
  return sections.map((section, index) => ({ ...section, order: index }));
}

type OptionItem = SurveyMultiSelectItem | SurveySingleSelectItem;
type SkipSource = SurveyRatingItem | SurveyYesNoItem | OptionItem;

function getPriorSkipSources(slot: SurveyTimingSlot, targetItemId: string): SkipSource[] {
  const prior: SkipSource[] = [];
  const orderedSections = [...slot.sections].sort((a, b) => a.order - b.order);
  for (const section of orderedSections) {
    for (const item of section.items) {
      if (item.id === targetItemId) return prior;
      if (item.type !== "text") prior.push(item);
    }
  }
  return prior;
}

function defaultSkipAnswer(source: SkipSource): SurveySkipCondition["answer"] {
  if (source.type === "yesno") return true;
  if (source.type === "rating") return 1;
  return source.options[0]?.id ?? "";
}

function clearSkipConditions(
  sections: SurveySection[],
  shouldClear: (condition: SurveySkipCondition) => boolean
): SurveySection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.skipIf && shouldClear(item.skipIf) ? { ...item, skipIf: undefined } : item
    ) as SurveyItem[],
  }));
}

function SkipLogicEditor({
  item,
  sources,
  onChange,
}: {
  item: SurveyItem;
  sources: SkipSource[];
  onChange: (condition: SurveySkipCondition | undefined) => void;
}) {
  const source = sources.find((candidate) => candidate.id === item.skipIf?.questionId);
  const enabled = item.skipIf != null;

  return (
    <div className="ml-20 border-t pt-2 space-y-2">
      <label className="flex items-center gap-1.5 text-xs">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!enabled && sources.length === 0}
          onChange={(event) => {
            if (!event.target.checked) {
              onChange(undefined);
              return;
            }
            const initialSource = sources[sources.length - 1];
            if (initialSource) {
              onChange({ questionId: initialSource.id, answer: defaultSkipAnswer(initialSource) });
            }
          }}
        />
        Skip this question based on an earlier answer
      </label>

      {enabled && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Earlier question</label>
            <select
              className="w-full rounded border px-2 py-1 text-xs"
              value={source?.id ?? ""}
              onChange={(event) => {
                const nextSource = sources.find((candidate) => candidate.id === event.target.value);
                if (nextSource) {
                  onChange({ questionId: nextSource.id, answer: defaultSkipAnswer(nextSource) });
                }
              }}
            >
              {!source && <option value="">Select a question</option>}
              {sources.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.label}</option>
              ))}
            </select>
          </div>

          {source && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Answer that skips this question</label>
              <select
                className="w-full rounded border px-2 py-1 text-xs"
                value={String(item.skipIf?.answer ?? "")}
                onChange={(event) => {
                  let answer: SurveySkipCondition["answer"] = event.target.value;
                  if (source.type === "yesno") answer = event.target.value === "true";
                  if (source.type === "rating" && event.target.value !== "NA") answer = Number(event.target.value);
                  onChange({ questionId: source.id, answer });
                }}
              >
                {source.type === "yesno" && (
                  <>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </>
                )}
                {source.type === "rating" && (
                  <>
                    {[1, 2, 3, 4, 5, 6, 7].map((value) => <option key={value} value={value}>{value}</option>)}
                    {source.allowNA && <option value="NA">N/A</option>}
                  </>
                )}
                {(source.type === "multiselect" || source.type === "singleselect") && (
                  <>
                    {source.options.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                    {source.type === "multiselect" && source.hasOther && <option value="__other">Other</option>}
                    {source.allowNA && <option value="NA">N/A</option>}
                  </>
                )}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SurveyConfigPage() {
  const { data, isLoading, isError, error } = useSurveyConfig();
  const updateMutation = useUpdateSurveyConfig();
  const participants = useParticipants();

  const [timing, setTiming] = useState<SurveyTimingSlot[]>([]);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [selectedParticipantId, setSelectedParticipantId] = useState("");
  const schedule = useParticipantSurveySchedule(selectedParticipantId);
  const updateSchedule = useUpdateParticipantSurveySchedule(selectedParticipantId);
  const [timeDraft, setTimeDraft] = useState<{
    participantId: string;
    values: Record<string, string>;
  }>({ participantId: "", values: {} });
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);

  const participantLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of participants.data?.data ?? []) {
      map[p.user_id] = p.username || p.user_id.slice(0, 8);
    }
    return map;
  }, [participants.data]);

  const scheduleSlots = useMemo(
    () => schedule.data?.data.timing ?? [],
    [schedule.data?.data.timing],
  );
  const scheduleTimes = useMemo(
    () =>
      Object.fromEntries(
        scheduleSlots.map((slot) => [
          slot.id,
          `${String(slot.scheduledHour).padStart(2, "0")}:${String(slot.scheduledMinute).padStart(2, "0")}`,
        ]),
      ),
    [scheduleSlots],
  );
  const times =
    timeDraft.participantId === selectedParticipantId
      ? { ...scheduleTimes, ...timeDraft.values }
      : scheduleTimes;

  const renderParticipantValue = (val: unknown): ReactNode => {
    if (!val) return null;
    const s = String(val);
    return participantLabels[s] ?? s.slice(0, 8);
  };

  useEffect(() => {
    if (data?.data) {
      // Server normalizes old configs so every slot carries its questions.
      setTiming(
        (data.data.timing ?? []).map((slot) => ({
          ...slot,
          // DynamoDB numbers are serialized as strings by the API, so restore
          // weekday values before passing them to the numeric dropdown.
          sections: normalizeSections(slot.sections ?? data.data.sections ?? []),
          open_ended: slot.open_ended ?? data.data.open_ended ?? DEFAULT_OPEN_ENDED,
        }))
      );
    }
  }, [data]);

  if (isLoading) return <Spinner />;
  if (isError) return <ErrorDisplay message={error.message} onRetry={() => {}} />;

  const version = data?.data?.version ?? 0;

  async function handleSave() {
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await updateMutation.mutateAsync({ timing });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    }
  }

  async function saveSchedule() {
    if (!selectedParticipantId) return;
    setScheduleError(null);
    setScheduleSaved(false);
    const missingSlot = scheduleSlots.find((slot) => !times[slot.id]);
    if (missingSlot) {
      setScheduleError(`Select a time for ${missingSlot.label}.`);
      return;
    }
    const participantTiming = scheduleSlots.map((slot) => {
      const [hour, minute] = times[slot.id].split(":").map(Number);
      return { id: slot.id, scheduledHour: hour, scheduledMinute: minute };
    });
    try {
      await updateSchedule.mutateAsync({ timing: participantTiming });
      setTimeDraft({ participantId: selectedParticipantId, values: {} });
      setScheduleSaved(true);
    } catch (err) {
      setScheduleError(
        err instanceof Error ? err.message : "Failed to save survey schedule",
      );
    }
  }

  function updateSlot(slotId: string, patch: Partial<SurveyTimingSlot>) {
    setTiming((prev) => prev.map((t) => (t.id !== slotId ? t : { ...t, ...patch })));
  }

  function updateSlotSections(slotId: string, update: (sections: SurveySection[]) => SurveySection[]) {
    setTiming((prev) => prev.map((t) => (t.id !== slotId ? t : { ...t, sections: update(t.sections) })));
  }

  function moveSection(slotId: string, sectionId: string, direction: -1 | 1) {
    updateSlotSections(slotId, (sections) => {
      const ordered = [...sections].sort((a, b) => a.order - b.order);
      const index = ordered.findIndex((section) => section.id === sectionId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= ordered.length) return sections;

      const next = [...ordered];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return renumberSections(next);
    });
  }

  function moveItem(slotId: string, sectionId: string, itemId: string, direction: -1 | 1) {
    updateSlotSections(slotId, (sections) =>
      sections.map((section) => {
        if (section.id !== sectionId) return section;
        const index = section.items.findIndex((item) => item.id === itemId);
        const nextIndex = index + direction;
        if (index < 0 || nextIndex < 0 || nextIndex >= section.items.length) return section;

        const items = [...section.items];
        [items[index], items[nextIndex]] = [items[nextIndex], items[index]];
        return { ...section, items };
      })
    );
  }

  function updateItemField(slotId: string, sectionId: string, itemId: string, field: string, value: string) {
    updateSlotSections(slotId, (sections) =>
      sections.map((s) =>
        s.id !== sectionId ? s : {
          ...s,
          items: s.items.map((item) => (item.id !== itemId ? item : { ...item, [field]: value })),
        }
      )
    );
  }

  function updateItemSkip(
    slotId: string,
    sectionId: string,
    itemId: string,
    skipIf: SurveySkipCondition | undefined
  ) {
    updateSlotSections(slotId, (sections) =>
      sections.map((section) =>
        section.id !== sectionId ? section : {
          ...section,
          items: section.items.map((item) =>
            item.id !== itemId ? item : { ...item, skipIf }
          ) as SurveyItem[],
        }
      )
    );
  }

  function updateItemCapability(
    slotId: string,
    sectionId: string,
    itemId: string,
    patch: { allowNA?: boolean; hasOther?: boolean; required?: boolean },
    invalidSkipAnswer?: SurveySkipCondition["answer"]
  ) {
    updateSlotSections(slotId, (sections) => {
      const updated = sections.map((section) =>
        section.id !== sectionId ? section : {
          ...section,
          items: section.items.map((item) =>
            item.id !== itemId ? item : { ...item, ...patch }
          ) as SurveyItem[],
        }
      );
      return invalidSkipAnswer === undefined
        ? updated
        : clearSkipConditions(
            updated,
            (condition) =>
              condition.questionId === itemId && condition.answer === invalidSkipAnswer
          );
    });
  }

  function removeItem(slotId: string, sectionId: string, itemId: string) {
    updateSlotSections(slotId, (sections) =>
      clearSkipConditions(
        sections.map((section) => ({
          ...section,
          items: section.items.filter((item) => section.id !== sectionId || item.id !== itemId),
        })),
        (condition) => condition.questionId === itemId
      )
    );
  }

  function addRatingItem(slotId: string, sectionId: string) {
    const newItem: SurveyRatingItem = {
      id: newId("item"),
      type: "rating",
      label: "New question",
      scaleMin: "Not at all",
      scaleMax: "Very much",
      required: false,
    };
    updateSlotSections(slotId, (sections) =>
      sections.map((s) => (s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }))
    );
  }

  function addYesNoItem(slotId: string, sectionId: string) {
    const newItem: SurveyYesNoItem = {
      id: newId("item"),
      type: "yesno",
      label: "New yes/no question",
      required: false,
      followUps: [],
    };
    updateSlotSections(slotId, (sections) =>
      sections.map((s) => (s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }))
    );
  }

  function addMultiSelectItem(slotId: string, sectionId: string) {
    const newItem: SurveyMultiSelectItem = {
      id: newId("item"),
      type: "multiselect",
      label: "New multi-select question",
      options: [{ id: newId("opt"), label: "Option 1" }],
      hasOther: false,
      allowNA: false,
      required: false,
    };
    updateSlotSections(slotId, (sections) =>
      sections.map((s) => (s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }))
    );
  }

  function addTextItem(slotId: string, sectionId: string) {
    const newItem: SurveyTextItem = {
      id: newId("item"),
      type: "text",
      label: "New text question",
      placeholder: "Type your response here",
      required: false,
    };
    updateSlotSections(slotId, (sections) =>
      sections.map((s) => (s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }))
    );
  }

  function addSingleSelectItem(slotId: string, sectionId: string) {
    const newItem: SurveySingleSelectItem = {
      id: newId("item"),
      type: "singleselect",
      label: "New single-select question",
      options: [{ id: newId("opt"), label: "Option 1" }],
      allowNA: false,
      required: false,
    };
    updateSlotSections(slotId, (sections) =>
      sections.map((s) => (s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }))
    );
  }

  function addOption(slotId: string, sectionId: string, itemId: string) {
    updateSlotSections(slotId, (sections) =>
      sections.map((s) =>
        s.id !== sectionId ? s : {
          ...s,
          items: s.items.map((item) =>
            item.id !== itemId || (item.type !== "multiselect" && item.type !== "singleselect") ? item : {
              ...item,
              options: [...item.options, { id: newId("opt"), label: `Option ${item.options.length + 1}` }],
            }
          ),
        }
      )
    );
  }

  function updateOption(slotId: string, sectionId: string, itemId: string, optionId: string, label: string) {
    updateSlotSections(slotId, (sections) =>
      sections.map((s) =>
        s.id !== sectionId ? s : {
          ...s,
          items: s.items.map((item) =>
            item.id !== itemId || (item.type !== "multiselect" && item.type !== "singleselect") ? item : {
              ...item,
              options: item.options.map((opt) => (opt.id !== optionId ? opt : { ...opt, label })),
            }
          ),
        }
      )
    );
  }

  function removeOption(slotId: string, sectionId: string, itemId: string, optionId: string) {
    updateSlotSections(slotId, (sections) => {
      let removed = false;
      const updated = sections.map((s) =>
        s.id !== sectionId ? s : {
          ...s,
          items: s.items.map((item) => {
            // The backend requires at least one option per select item.
            if (
              item.id !== itemId ||
              (item.type !== "multiselect" && item.type !== "singleselect") ||
              item.options.length <= 1
            ) {
              return item;
            }
            const options = item.options.filter((opt) => opt.id !== optionId);
            if (options.length === item.options.length) return item;
            removed = true;
            return {
              ...item,
              options,
            };
          }),
        }
      );
      if (!removed) return updated;
      return clearSkipConditions(
        updated,
        (condition) => condition.questionId === itemId && condition.answer === optionId
      );
    });
  }

  function copyQuestionsFrom(targetSlotId: string, sourceSlotId: string) {
    const source = timing.find((t) => t.id === sourceSlotId);
    if (!source) return;
    setTiming((prev) =>
      prev.map((t) =>
        t.id !== targetSlotId ? t : {
          ...t,
          sections: deepCopySections(source.sections),
          open_ended: { ...source.open_ended },
        }
      )
    );
  }

  function addTimingSlot() {
    // New slots start with a copy of the first slot's questions (the backend
    // rejects slots without questions).
    const template = timing[0];
    setTiming((prev) => [
      ...prev,
      {
        id: newId("slot"),
        label: "New Slot",
        scheduledHour: 12,
        scheduledMinute: 0,
        pastDueHour: 15,
        sections: template ? deepCopySections(template.sections) : [],
        open_ended: template ? { ...template.open_ended } : { ...DEFAULT_OPEN_ENDED },
      },
    ]);
  }

  function addSection(slotId: string) {
    const sectionId = newId("section");
    const firstItem: SurveyRatingItem = {
      id: newId("item"),
      type: "rating",
      label: "New question",
      scaleMin: "Not at all",
      scaleMax: "Very much",
      required: false,
    };
    updateSlotSections(slotId, (sections) => {
      const nextOrder = Math.max(-1, ...sections.map((section) => section.order)) + 1;
      return [
        ...sections,
        {
          id: sectionId,
          title: "New section",
          description: "",
          randomize: false,
          order: nextOrder,
          items: [firstItem],
        },
      ];
    });
    setExpandedSection(`${slotId}:${sectionId}`);
  }

  function removeSection(slotId: string, sectionId: string) {
    updateSlotSections(slotId, (sections) => {
      if (sections.length <= 1) return sections;
      const removedItemIds = new Set(
        sections.find((section) => section.id === sectionId)?.items.map((item) => item.id) ?? []
      );
      return clearSkipConditions(
        renumberSections(sections.filter((section) => section.id !== sectionId).sort((a, b) => a.order - b.order)),
        (condition) => removedItemIds.has(condition.questionId)
      );
    });
    if (expandedSection === `${slotId}:${sectionId}`) {
      setExpandedSection(null);
    }
  }

  function removeTimingSlot(id: string) {
    setTiming((prev) => prev.filter((t) => t.id !== id));
  }

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">Survey Config</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Current version: {version} · each slot has its own questions · all times are participant-local
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveSuccess && <span className="text-sm text-green-600">Saved successfully</span>}
          {saveError && <span className="text-sm text-red-600">{saveError}</span>}
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Participant Survey Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1 max-w-xs">
            <label className="text-xs font-medium text-muted-foreground">
              Participant
            </label>
            <Select
              value={selectedParticipantId}
              onValueChange={(val) => {
                setSelectedParticipantId(val ?? "");
                setScheduleSaved(false);
                setScheduleError(null);
              }}
              disabled={participants.isLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    participants.isLoading
                      ? "Loading participants..."
                      : "Select a participant"
                  }
                >
                  {renderParticipantValue}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {(participants.data?.data ?? []).map((p) => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.username || p.user_id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {participants.isError && (
            <div className="text-sm text-red-600">
              {participants.error instanceof Error
                ? participants.error.message
                : "Failed to load participants"}
            </div>
          )}

          {selectedParticipantId ? (
            <>
              <div className="text-sm text-muted-foreground">
                Participant local time ·{" "}
                {schedule.data?.data.customized
                  ? "Custom schedule"
                  : "Using global schedule"}
                {schedule.data?.data.updated_at
                  ? ` · Updated ${formatDate(schedule.data.data.updated_at)}`
                  : ""}
              </div>

              {schedule.isError && (
                <div className="text-sm text-red-600">
                  {schedule.error instanceof Error
                    ? schedule.error.message
                    : "Failed to load survey schedule"}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                {scheduleSlots.map((slot) => (
                  <label key={slot.id} className="space-y-2 text-sm font-medium">
                    <span>{slot.label}</span>
                    <Input
                      type="time"
                      value={times[slot.id] ?? ""}
                      onChange={(event) => {
                        setTimeDraft((prev) => ({
                          participantId: selectedParticipantId,
                          values: {
                            ...(prev.participantId === selectedParticipantId
                              ? prev.values
                              : {}),
                            [slot.id]: event.target.value,
                          },
                        }));
                        setScheduleSaved(false);
                        setScheduleError(null);
                      }}
                      disabled={schedule.isLoading || updateSchedule.isPending}
                    />
                  </label>
                ))}
              </div>

              {scheduleError && (
                <div className="text-sm text-red-600">{scheduleError}</div>
              )}
              {scheduleSaved && (
                <div className="text-sm text-green-700">
                  Survey schedule saved.
                </div>
              )}

              <Button
                type="button"
                onClick={saveSchedule}
                disabled={
                  schedule.isLoading ||
                  scheduleSlots.length === 0 ||
                  updateSchedule.isPending
                }
              >
                {updateSchedule.isPending ? "Saving..." : "Save Schedule"}
              </Button>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              Select a participant to view or customize their survey schedule.
            </div>
          )}
        </CardContent>
      </Card>

      {timing.map((slot) => (
        <Card key={slot.id}>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <input
                  className="rounded border px-3 py-1.5 text-sm font-medium w-40"
                  value={slot.label}
                  onChange={(e) => updateSlot(slot.id, { label: e.target.value })}
                />
                <CardTitle className="text-sm font-normal text-muted-foreground">
                  {slot.sections.reduce((n, s) => n + s.items.length, 0)} questions
                </CardTitle>
              </div>
              <div className="flex items-center gap-3">
                {timing.length > 1 && (
                  <select
                    className="rounded border px-2 py-1.5 text-xs"
                    value=""
                    onChange={(e) => e.target.value && copyQuestionsFrom(slot.id, e.target.value)}
                  >
                    <option value="">Copy questions from…</option>
                    {timing
                      .filter((t) => t.id !== slot.id)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                )}
                <button onClick={() => removeTimingSlot(slot.id)} className="text-sm text-red-500 hover:text-red-700">
                  Remove slot
                </button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Schedule */}
            <div className="grid grid-cols-3 gap-3 max-w-md">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Hour (0–23)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={slot.scheduledHour}
                  onChange={(e) => updateSlot(slot.id, { scheduledHour: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Minute (0–59)</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={slot.scheduledMinute}
                  onChange={(e) => updateSlot(slot.id, { scheduledMinute: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Past-due hour (0–24)</label>
                <input
                  type="number"
                  min={0}
                  max={24}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  value={slot.pastDueHour}
                  onChange={(e) => updateSlot(slot.id, { pastDueHour: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-3">
              {[...slot.sections].sort((a, b) => a.order - b.order).map((section, sectionIndex) => {
                const key = `${slot.id}:${section.id}`;
                return (
                  <div key={section.id} className="rounded-lg border">
                    <div
                      className="cursor-pointer select-none px-4 py-3 flex items-center justify-between"
                      onClick={() => setExpandedSection(expandedSection === key ? null : key)}
                    >
                      <span className="text-sm font-medium">{section.title}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{section.items.length} items</span>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          title={`Move ${section.title} up`}
                          aria-label={`Move ${section.title} up`}
                          disabled={sectionIndex === 0}
                          onClick={(event) => {
                            event.stopPropagation();
                            moveSection(slot.id, section.id, -1);
                          }}
                        >
                          <ArrowUp aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          title={`Move ${section.title} down`}
                          aria-label={`Move ${section.title} down`}
                          disabled={sectionIndex === slot.sections.length - 1}
                          onClick={(event) => {
                            event.stopPropagation();
                            moveSection(slot.id, section.id, 1);
                          }}
                        >
                          <ArrowDown aria-hidden="true" />
                        </Button>
                        {slot.sections.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            title={`Remove ${section.title}`}
                            aria-label={`Remove ${section.title}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeSection(slot.id, section.id);
                            }}
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {expandedSection === key && (
                      <div className="px-4 pb-4 space-y-3">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Title</label>
                          <input
                            className="w-full rounded border px-3 py-1.5 text-sm"
                            value={section.title}
                            onChange={(e) =>
                              updateSlotSections(slot.id, (sections) =>
                                sections.map((s) => (s.id !== section.id ? s : { ...s, title: e.target.value }))
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Description</label>
                          <input
                            className="w-full rounded border px-3 py-1.5 text-sm"
                            value={section.description}
                            onChange={(e) =>
                              updateSlotSections(slot.id, (sections) =>
                                sections.map((s) => (s.id !== section.id ? s : { ...s, description: e.target.value }))
                              )
                            }
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`randomize-${key}`}
                            checked={section.randomize}
                            onChange={(e) =>
                              updateSlotSections(slot.id, (sections) =>
                                sections.map((s) => (s.id !== section.id ? s : { ...s, randomize: e.target.checked }))
                              )
                            }
                          />
                          <label htmlFor={`randomize-${key}`} className="text-sm">Randomize item order</label>
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">Days shown</label>
                          <WeekdayDropdown
                            value={section.weekdays}
                            onChange={(weekdays) =>
                              updateSlotSections(slot.id, (sections) =>
                                sections.map((s) => (s.id !== section.id ? s : { ...s, weekdays }))
                              )
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          {section.items.map((item, itemIndex) => (
                            <div key={item.id} className="rounded border p-3 space-y-2 bg-muted/30">
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-medium text-muted-foreground mt-1.5 w-20 shrink-0">{item.type}</span>
                                <input
                                  className="flex-1 rounded border px-2 py-1 text-sm"
                                  value={item.label}
                                  onChange={(e) => updateItemField(slot.id, section.id, item.id, "label", e.target.value)}
                                />
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  title="Move question up"
                                  aria-label={`Move ${item.label} up`}
                                  disabled={itemIndex === 0}
                                  onClick={() => moveItem(slot.id, section.id, item.id, -1)}
                                >
                                  <ArrowUp aria-hidden="true" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  title="Move question down"
                                  aria-label={`Move ${item.label} down`}
                                  disabled={itemIndex === section.items.length - 1}
                                  onClick={() => moveItem(slot.id, section.id, item.id, 1)}
                                >
                                  <ArrowDown aria-hidden="true" />
                                </Button>
                                <button
                                  onClick={() => removeItem(slot.id, section.id, item.id)}
                                  className="text-xs text-red-500 hover:text-red-700 shrink-0"
                                >
                                  Remove
                                </button>
                              </div>
                              <label className="ml-20 flex items-center gap-1.5 text-xs">
                                <input
                                  type="checkbox"
                                  checked={item.required ?? false}
                                  onChange={(e) =>
                                    updateItemCapability(
                                      slot.id,
                                      section.id,
                                      item.id,
                                      { required: e.target.checked }
                                    )
                                  }
                                />
                                Required
                              </label>
                              {item.type === "rating" && (
                                <>
                                  <div className="flex gap-2 ml-20">
                                    <div className="flex-1 space-y-1">
                                      <label className="text-xs text-muted-foreground">Min label</label>
                                      <input
                                        className="w-full rounded border px-2 py-1 text-xs"
                                        value={item.scaleMin}
                                        onChange={(e) => updateItemField(slot.id, section.id, item.id, "scaleMin", e.target.value)}
                                      />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                      <label className="text-xs text-muted-foreground">Max label</label>
                                      <input
                                        className="w-full rounded border px-2 py-1 text-xs"
                                        value={item.scaleMax}
                                        onChange={(e) => updateItemField(slot.id, section.id, item.id, "scaleMax", e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-4 ml-20">
                                    <label className="flex items-center gap-1.5 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={item.allowNA ?? false}
                                        onChange={(e) => {
                                          const allowNA = e.target.checked;
                                          updateItemCapability(
                                            slot.id,
                                            section.id,
                                            item.id,
                                            { allowNA },
                                            allowNA ? undefined : "NA"
                                          )
                                        }}
                                      />
                                      Allow N/A
                                    </label>
                                    <label className="flex items-center gap-1.5 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={item.fixed ?? false}
                                        onChange={(e) =>
                                          updateSlotSections(slot.id, (sections) =>
                                            sections.map((s) =>
                                              s.id !== section.id ? s : {
                                                ...s,
                                                items: s.items.map((it) => it.id !== item.id ? it : { ...it, fixed: e.target.checked }) as SurveyItem[],
                                              }
                                            )
                                          )
                                        }
                                      />
                                      Fixed (no shuffle)
                                    </label>
                                  </div>
                                </>
                              )}
                              {(item.type === "multiselect" || item.type === "singleselect") && (
                                <div className="ml-20 space-y-2">
                                  <div className="flex gap-4">
                                    {item.type === "multiselect" && (
                                      <label className="flex items-center gap-1.5 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={item.hasOther ?? false}
                                          onChange={(e) => {
                                            const hasOther = e.target.checked;
                                            updateItemCapability(
                                              slot.id,
                                              section.id,
                                              item.id,
                                              { hasOther },
                                              hasOther ? undefined : "__other"
                                            )
                                          }}
                                        />
                                        Has "Other" option
                                      </label>
                                    )}
                                    <label className="flex items-center gap-1.5 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={item.allowNA ?? false}
                                        onChange={(e) => {
                                          const allowNA = e.target.checked;
                                          updateItemCapability(
                                            slot.id,
                                            section.id,
                                            item.id,
                                            { allowNA },
                                            allowNA ? undefined : "NA"
                                          )
                                        }}
                                      />
                                      Allow N/A
                                    </label>
                                  </div>
                                  <div className="space-y-1">
                                    {item.options.map((opt) => (
                                      <div key={opt.id} className="flex items-center gap-2">
                                        <input
                                          className="flex-1 rounded border px-2 py-1 text-xs"
                                          value={opt.label}
                                          onChange={(e) => updateOption(slot.id, section.id, item.id, opt.id, e.target.value)}
                                        />
                                        <button
                                          onClick={() => removeOption(slot.id, section.id, item.id, opt.id)}
                                          className="text-xs text-red-500 hover:text-red-700 shrink-0"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      onClick={() => addOption(slot.id, section.id, item.id)}
                                      className="text-xs text-blue-500 hover:text-blue-700"
                                    >
                                      + Add option
                                    </button>
                                  </div>
                                </div>
                              )}
                              {item.type === "text" && (
                                <div className="ml-20 space-y-1">
                                  <label className="text-xs text-muted-foreground">Placeholder</label>
                                  <input
                                    className="w-full rounded border px-2 py-1 text-xs"
                                    value={item.placeholder ?? ""}
                                    onChange={(e) => updateItemField(slot.id, section.id, item.id, "placeholder", e.target.value)}
                                  />
                                </div>
                              )}
                              <SkipLogicEditor
                                item={item}
                                sources={getPriorSkipSources(slot, item.id)}
                                onChange={(skipIf) => updateItemSkip(slot.id, section.id, item.id, skipIf)}
                              />
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => addRatingItem(slot.id, section.id)}>
                            + Rating item
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addYesNoItem(slot.id, section.id)}>
                            + Yes/No item
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addMultiSelectItem(slot.id, section.id)}>
                            + Multi-select item
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addSingleSelectItem(slot.id, section.id)}>
                            + Single-select item
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addTextItem(slot.id, section.id)}>
                            + Text item
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={() => addSection(slot.id)}>
                <Plus aria-hidden="true" />
                Add section
              </Button>
            </div>

            {/* Open-ended */}
            <div className="rounded-lg border px-4 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`open-ended-${slot.id}`}
                  checked={slot.open_ended.enabled}
                  onChange={(e) => updateSlot(slot.id, { open_ended: { ...slot.open_ended, enabled: e.target.checked } })}
                />
                <label htmlFor={`open-ended-${slot.id}`} className="text-sm font-medium">Open-ended question</label>
              </div>
              {slot.open_ended.enabled && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className="rounded border px-2 py-1 text-sm"
                    placeholder="Title"
                    value={slot.open_ended.title}
                    onChange={(e) => updateSlot(slot.id, { open_ended: { ...slot.open_ended, title: e.target.value } })}
                  />
                  <input
                    className="rounded border px-2 py-1 text-sm"
                    placeholder="Placeholder text"
                    value={slot.open_ended.placeholder}
                    onChange={(e) => updateSlot(slot.id, { open_ended: { ...slot.open_ended, placeholder: e.target.value } })}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      <Button variant="outline" onClick={addTimingSlot}>
        <Plus aria-hidden="true" />
        Add slot
      </Button>
    </div>
  );
}
